/**
===================================================
Module: frontend.src.components.visualizer.VisualizationControls.tsx
Description: Visualization controls component for 3D scene manipulation in VSLAM visualizer
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useCallback } from "react";
import { useVisualizer } from "../../contexts/VisualizerContext";

interface VisualizationControlsProps {
  onResetCamera: () => void;
}

const VisualizationControls: React.FC<VisualizationControlsProps> = ({
  onResetCamera,
}) => {
  const { state, updateVisualizationSettings } = useVisualizer();
  const { visualizationSettings, error } = state;

  const [isCollapsed, setIsCollapsed] = useState(false);

  const togglePanel = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  const handleVisibilityChange = useCallback(
    (setting: keyof typeof visualizationSettings, value: boolean) => {
      updateVisualizationSettings({ [setting]: value });
    },
    [updateVisualizationSettings],
  );

  const handleSizeChange = useCallback(
    (setting: keyof typeof visualizationSettings, value: number) => {
      updateVisualizationSettings({ [setting]: value });
    },
    [updateVisualizationSettings],
  );

  return (
    <>
      <div className={`controls-panel ${isCollapsed ? "collapsed" : ""}`}>
        <h3 className="controls-panel-title">コントロールパネル</h3>

        <div className="control-section">
          <h4 className="control-section-title">表示</h4>

          <div className="visibility-control">
            <label className="visibility-label">
              <input
                type="checkbox"
                className="visibility-checkbox"
                checked={visualizationSettings.showLandmarks}
                onChange={(e) =>
                  handleVisibilityChange("showLandmarks", e.target.checked)
                }
              />
              特徴点
            </label>
          </div>

          <div className="visibility-control">
            <label className="visibility-label">
              <input
                type="checkbox"
                className="visibility-checkbox"
                checked={visualizationSettings.showFrustums}
                onChange={(e) =>
                  handleVisibilityChange("showFrustums", e.target.checked)
                }
              />
              キーフレーム
            </label>
          </div>

          <div className="visibility-control">
            <label className="visibility-label">
              <input
                type="checkbox"
                className="visibility-checkbox"
                checked={visualizationSettings.showTrajectory}
                onChange={(e) =>
                  handleVisibilityChange("showTrajectory", e.target.checked)
                }
              />
              移動軌跡
            </label>
          </div>

          <div className="visibility-control">
            <label className="visibility-label">
              <input
                type="checkbox"
                className="visibility-checkbox"
                checked={visualizationSettings.showDensePoints}
                onChange={(e) =>
                  handleVisibilityChange("showDensePoints", e.target.checked)
                }
              />
              点群
            </label>
          </div>
        </div>

        <div className="control-section">
          <h4 className="control-section-title">サイズ</h4>

          <div className="size-control">
            <label className="size-control-label">特徴点のサイズ</label>
            <input
              type="range"
              className="size-control-input"
              min="1"
              max="10"
              step="1"
              value={visualizationSettings.sparsePointSizeUI}
              onChange={(e) =>
                handleSizeChange("sparsePointSizeUI", parseInt(e.target.value))
              }
            />
            <span className="size-control-value">
              {visualizationSettings.sparsePointSizeUI}
            </span>
          </div>

          <div className="size-control">
            <label className="size-control-label">点群のサイズ</label>
            <input
              type="range"
              className="size-control-input"
              min="1"
              max="10"
              step="1"
              value={visualizationSettings.densePointSizeUI}
              onChange={(e) =>
                handleSizeChange("densePointSizeUI", parseInt(e.target.value))
              }
            />
            <span className="size-control-value">
              {visualizationSettings.densePointSizeUI}
            </span>
          </div>

          <div className="size-control">
            <label className="size-control-label">キーフレームのサイズ</label>
            <input
              type="range"
              className="size-control-input"
              min="1"
              max="10"
              step="1"
              value={visualizationSettings.frustumScaleUI}
              onChange={(e) =>
                handleSizeChange("frustumScaleUI", parseInt(e.target.value))
              }
            />
            <span className="size-control-value">
              {visualizationSettings.frustumScaleUI}
            </span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button onClick={onResetCamera} className="reset-button">
          視点のリセット
        </button>
      </div>

      <button
        className="control-panel-toggle"
        onClick={togglePanel}
        title={isCollapsed ? "折りたたみ" : "折りたたみ"}
      >
        {isCollapsed ? (
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ transform: "scale(1.2)" }}
          >
            <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
          </svg>
        ) : (
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ transform: "scale(1.2)" }}
          >
            <path d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" />
          </svg>
        )}
      </button>
    </>
  );
};

export default VisualizationControls;
